import path from "node:path";
import { downloadImage, getFilenameFromUrl, Locator, Response, success, TaskState } from "@iflyrpa/share";
import { Page } from "playwright-core";
import { MockPublish } from "../../types";
import { TimeFormatter } from "../../utils";
import { ProxyAgent } from "../../utils/proxy";
import type { PublishAction } from "./index";

export const rpaAction_Server: PublishAction = async (task, params) => {
    task.logger.info("开始微信视频号发布（Server 模式）");
    const defaultPage = task.steelBrowserContext?.pages()[0];
    if (defaultPage) {
        // 如果 route 已经注册过，就不重复注册
        if (!(defaultPage as any)._routeRegistered) {
            (defaultPage as any)._routeRegistered = true;

            const blockedPatterns = ["**/apm-fe.weixin.com*", "**/t2.weixin.com*"].map(
                (pattern) => new RegExp(pattern.replace(/\*\*/g, ".*").replace(/\*/g, "[^/]*")),
            );

            await defaultPage.route("**/*", async (route) => {
                const req = route.request();
                const url = req.url();

                // 是否命中屏蔽规则
                const blocked = blockedPatterns.some((regex) => regex.test(url));
                if (!blocked) {
                    return route.continue();
                }

                if (req.method() === "OPTIONS") {
                    console.log("处理 OPTIONS 预检：", url);
                    await route.fulfill({
                        status: 204,
                        headers: {
                            "access-control-allow-origin": "*",
                            "access-control-allow-methods": "GET,POST,OPTIONS,PUT,DELETE",
                            "access-control-allow-headers": "*",
                        },
                        body: "",
                    });
                    return;
                }

                await route.fulfill({
                    status: 204,
                    body: "",
                });
            });
        }
    }

    const tmpCachePath = task.getTmpPath();
    task.logger?.info("==>进入RPA操作：任务开始,下载图片开始...");
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    const imagePromise = Promise.all(
        params.banners.map((url) => {
            const fileName = getFilenameFromUrl(url);
            return downloadImage(url, path.join(tmpCachePath, fileName));
        }),
    );

    let proxyUrl: string | undefined;
    if (params.proxyLoc) {
        task.logger?.info(`==> 开始获取代理信息：${params}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, params.proxyLoc, params.accountId);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }

    task.logger?.info("==>开始打开视频号页面...");
    const page = await task.createPage({
        show: task.debug,
        cookies: params.cookies,
        proxyUrl,
        url: params.url || "https://channels.weixin.qq.com/platform/post/finderNewLifeCreate",
        ...(params.viewport ? { viewport: params.viewport } : {}),
    });
    task.logger?.info("==>视频号页面打开成功");
    console.log(task.steelConnector?.getLive(task.sessionId || ""));
    await updateTaskState?.({
        state: TaskState.ACTION,
        connectAddress: task.steelConnector?.getProxyUrl(task.sessionId || "", "v1/sessions/debug"),
        sessionId: task.sessionId,
    });

    const clickTimeout = 1000 * 10;

    // 自动化脚本
    // ----------------------------------------------------------------------------------

    // 等待登录成功
    try {
        await page.waitForSelector(".post-edit-wrap", { state: "visible", timeout: 10000 });
        task.logger?.info("✓ 登录状态正常，找到编辑器容器");
    } catch (error) {
        task.logger?.error("✗ 未找到编辑器，可能登录失效");
        return { code: 414, message: "登录失效或页面加载异常", data: page.url() };
    }

    // 获取待上传图片的本地路径
    const images = await imagePromise;

    task.logger?.info(`==>图片下载完成，共 ${images.length} 张，开始上传图片...`);

    // 上传图片
    const uploadSelectors = [
        '.ant-upload-btn input[type="file"]',
        '.upload input[type="file"][accept*="image"]',
        '.upload-wrap input[type="file"]',
        'input[type="file"][accept*="image"]',
    ];

    let uploadInput: Locator | null = null;

    for (const selector of uploadSelectors) {
        const input = page.locator(selector);
        const count = await input.count();
        if (count > 0) {
            uploadInput = input.first();
            task.logger?.info(`找到上传输入框: ${selector}`);
            break;
        }
    }

    if (!uploadInput) {
        task.logger?.warn("未找到上传输入框，等待 3 秒后重试...");
        await page.waitForTimeout(3000);

        const anyFileInput = page.locator('input[type="file"]');
        const inputCount = await anyFileInput.count();

        if (inputCount > 0) {
            uploadInput = anyFileInput.first();
            task.logger?.info(`找到文件输入框（共 ${inputCount} 个）`);
        } else {
            if (task.debug) {
                const screenshotPath = path.join(tmpCachePath, `upload-error-${Date.now()}.png`);
                await page.screenshot({ path: screenshotPath, fullPage: true });
                task.logger?.error(`未找到上传输入框，已截图保存至: ${screenshotPath}`);
            }
            throw new Error("未找到图片上传输入框");
        }
    }

    await uploadInput.setInputFiles(images, { timeout: 60 * 1000 });
    task.logger?.info(`==>图片上传完成: ${images.length} 张`);

    // 等待图片处理
    await page.waitForTimeout(3000);

    // 填写标题
    if (params.title) {
        task.logger?.info(`填写标题: ${params.title}`);
        const titleInput = page.locator('input[placeholder*="填写标题"]');
        await titleInput.waitFor({ state: "visible", timeout: clickTimeout });
        await titleInput.click({ timeout: clickTimeout });
        await titleInput.clear({ timeout: 3000 });
        await titleInput.fill(params.title, { timeout: 5000 });
        task.logger?.info("==>标题填写完成");
    }

    // 填写描述和话题
    if (params.content) {
        task.logger?.info("填写描述和话题");
        const descEditor = page.locator(".input-editor");
        await descEditor.waitFor({ state: "visible", timeout: clickTimeout });
        await descEditor.click({ timeout: clickTimeout });
        await page.waitForTimeout(500);

        // 清空编辑器内容
        await descEditor.evaluate((el) => {
            el.textContent = "";
        });
        await page.waitForTimeout(300);
        task.logger?.info("已清空编辑器内容");

        // 模拟用户逐字输入，触发富文本编辑器的话题识别
        await descEditor.pressSequentially(params.content, { delay: 10 });
        await page.waitForTimeout(500);

        task.logger?.info("==>内容填写完成");
    }

    // 选择地点
    if (params.address?.poi_id) {
        task.logger?.info(`选择地点: ${params.address.name}`);
        const positionDisplayWrap = page.locator(".position-display-wrap");
        await positionDisplayWrap.click({ timeout: clickTimeout });
        await page.waitForTimeout(1000);
        await page
            .locator(".location-filter-wrap input")
            .fill(params.address?.name || params.address?.address || params.address?.city_name);
        await page.waitForTimeout(2000);
        const poperInstance = page.locator(".location-filter-wrap .common-option-list-wrap .option-item");
        await poperInstance.nth(1).waitFor();
        await poperInstance.nth(1).click({ timeout: clickTimeout });
        task.logger?.info("==>地点选择完成");
    }

    // 选择合集
    if (params.collection) {
        task.logger?.info(`选择合集: ${params.collection.collectionName}`);
        const instanceCollection = page.locator(".post-album-display-wrap");
        await instanceCollection.click({ timeout: clickTimeout });
        await page.waitForTimeout(1000);
        await page
            .locator(".post-album-wrap .option-item")
            .filter({ hasText: params.collection.collectionName })
            .first()
            .click({ force: true });
        task.logger?.info("==>合集选择完成");
    }

    // 填写链接
    if (params.link) {
        task.logger?.info(`添加链接: ${params.link.link}`);
        const instanceLink = page.locator(".link-display-wrap");
        await instanceLink.first().click({ timeout: clickTimeout });
        await page.waitForTimeout(500);
        const linkTypeName = params.link.urlType === 1 ? "公众号文章" : "红包封面";
        await page
            .locator(".post-link-wrap .link-list-options .link-option-item")
            .filter({ hasText: linkTypeName })
            .first()
            .click({ force: true });
        await page.waitForSelector(".link-input-wrap", { state: "visible", timeout: 3000 });
        const linkInput = page.locator('.link-input-wrap input[type="text"]');
        await linkInput.first().fill(params.link.link);
        task.logger?.info("==>链接添加完成");
    }

    // 选择音乐
    if (params.music) {
        task.logger?.info(`添加背景音乐: ${params.music.name}`);
        const musicFormItem = page.locator(".post-with-link").filter({ hasText: "音乐" });
        const musicDisplayWrap = musicFormItem.locator(".link-display-wrap").first();
        await musicDisplayWrap.click({ timeout: clickTimeout });
        const searchInput = musicFormItem.locator('.weui-desktop-search input[type="text"]').first();
        const searchTxt = `${params.music.name} - ${params.music.authorName}`;
        await searchInput.fill(searchTxt);
        await page.waitForTimeout(300);
        await searchInput.press("Enter");
        await page.waitForTimeout(2000);
        const bgmItemWrap = musicFormItem.locator(".bgm-item-wrap").filter({ hasText: searchTxt }).first();
        await bgmItemWrap.hover();
        await page.waitForTimeout(500);
        await bgmItemWrap.locator(".selected-icon").first().click({ force: true });
        task.logger?.info("==>背景音乐添加完成");
    }

    // 定时发布
    if (!params.isImmediatelyPublish && params.scheduledPublish) {
        task.logger?.info("设置定时发布");
        const timingRadio = page.locator(".weui-desktop-form__check-content").filter({ hasNotText: "不定时" }).first();
        await timingRadio.click({ timeout: clickTimeout });
        await page.waitForTimeout(500);
        const dateInput = page.locator('.weui-desktop-picker__date input[placeholder*="请选择发表时间"]');
        await dateInput.click({ timeout: clickTimeout });

        const dateD = TimeFormatter.format(params.scheduledPublish, "d");
        const nowMonth = TimeFormatter.format(Date.now(), "MM月");
        const nowMonthText = TimeFormatter.format(Date.now(), "M月");
        const month = TimeFormatter.format(params.scheduledPublish, "MM月");

        const monthLocator = page.locator("weui-desktop-picker__panel__label").filter({ hasText: month }).first();
        const monthCount = await monthLocator.count();

        if (monthCount === 0) {
            await page
                .locator(".weui-desktop-picker__panel__label")
                .filter({ hasText: nowMonth })
                .first()
                .click({ timeout: clickTimeout });
            await page.waitForTimeout(500);
            await page
                .locator(".weui-desktop-picker__table-row td a")
                .filter({ hasText: nowMonthText })
                .first()
                .click({ timeout: clickTimeout });
        }
        await page.waitForTimeout(500);
        await page
            .locator(".weui-desktop-picker__table-row td a")
            .filter({ hasText: dateD })
            .first()
            .click({ timeout: clickTimeout });

        await page
            .locator(".weui-desktop-form__input-wrp input[placeholder*='请选择时间']")
            .fill(TimeFormatter.format(params.scheduledPublish, "hh:mm"));
        await page.locator("i.weui-desktop-icon__time").click({ timeout: clickTimeout });

        // 关闭弹出框
        await page
            .locator(".post-time-wrap .form-item .label")
            .filter({ hasText: "发表时间" })
            .click({ timeout: clickTimeout });
        task.logger?.info("==>定时发布设置完成");
    }

    task.logger?.info("准备发布...");
    await page.waitForTimeout(300);

    if (MockPublish) {
        const message = "视频号模拟发布成功";
        const data = "123456789";

        await updateTaskState?.({
            state: TaskState.SUCCESS,
            result: {
                response: data,
            },
        });
        return success(data, message);
    }

    const publishResponse = await new Promise<{
        success: boolean;
        msg: string;
        data?: { id: string; objectId: string };
    }>((resolve) => {
        // 定义响应处理函数
        const handleResponse = async (response: Response) => {
            const url = response.url();
            if (url.includes("/post/post_create") || url.includes("/post/post_draft")) {
                const jsonResponse = await response.json();
                page.off("response", handleResponse);

                const articleId = jsonResponse.object?.id || jsonResponse.data?.objectId || "";
                const message = jsonResponse?.errMsg || "";
                const errCode = jsonResponse.data?.baseResp?.errcode ?? jsonResponse.errCode;

                if (errCode === 0) {
                    resolve({
                        success: true,
                        msg: message || "发布成功",
                        data: { id: articleId, objectId: articleId },
                    });
                } else {
                    let errorMessage = message;
                    if (errCode === -11224) {
                        errorMessage = "视频号管理员完成实名且绑定手机号后才可以发表";
                    } else if (errCode === 300333 || errCode === 300334) {
                        errorMessage = "登录失效";
                    } else if (errCode === 300330) {
                        errorMessage = "未登录";
                    } else if (errCode === 300002) {
                        errorMessage = "官方平台在校验音乐/位置/定时信息时失败了，请重新编辑后发布";
                    }
                    resolve({ success: false, msg: errorMessage });
                }
            }
        };

        // 开始监听响应事件
        page.on("response", handleResponse);

        // task._timerRecord.PrePublish = Date.now();

        // 点击发布按钮
        const publishText = params.publishType === "1" ? "发表" : "保存草稿";
        page.locator(".form-btns .weui-desktop-btn")
            .filter({ hasText: publishText })
            .first()
            .click({ timeout: clickTimeout });

        // 添加整体超时保护
        setTimeout(() => {
            page.off("response", handleResponse);
            resolve({ success: false, msg: "发布超时" });
        }, 10000);
    });

    if (!publishResponse?.success) {
        const msg = `发布失败：${publishResponse?.msg || "未知错误"}`;
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: msg,
        });
        await page.close();
        return { code: 414, message: msg, data: "" };
    }

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: {
            response: publishResponse?.data?.id || "",
        },
    });

    // 关闭页面
    await page.close();
    return success(publishResponse?.data?.id || "", publishResponse?.msg || "发布成功");
};
